// src/store/productSlice.ts
import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { Product, Category, ProductDictionary, StoreData } from '../interfaces';
import ProductService from '../services/productService';
import 'react-toastify/dist/ReactToastify.css';
import { toast } from 'react-toastify';
import productService from '../services/productService';
// Async thunk to fetch products from ProductService
export const fetchProducts = createAsyncThunk(
  'products/fetchProducts',
  async ({
    x_pagination_cursor,
    filters,
  }: {
    x_pagination_cursor?: string;
    filters?: {
      keyword?: string | null;
      categoryIds?: number[];
      tagName?: string | null;
      isActive?: boolean;
      isOutOfStock?: boolean;
    };
  }) => {
    const products = await ProductService.getUserProducts({
      x_pagination_cursor: x_pagination_cursor || '',
      filters: filters ? filters : {},
    });
    return products;
  }
);

export const fetchCategories = createAsyncThunk<Category[]>(
  'products/fetchCategories',
  async () => {
    const categories = await ProductService.getCategories();
    return categories;
  }
);
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const fetchStores = createAsyncThunk<any, string | number>(
  'public/stores',
  async (companyId: string | number) => {
    const stores = await productService.getPublicStores(companyId);
    return stores;
  }
);

//get Store Banner
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const fetchStoreBanner = createAsyncThunk<any, string | number>(
  'public/storeBanner',
  async (companyId: string | number) => {
    const banner = await productService.getPublicBanner(companyId);
    return banner;
  }
);

export const fetchStoreInfo = createAsyncThunk<
  StoreData | null,
  string | number
>('public/storeInfo', async (storeId: string | number) => {
  const stores = await productService.getPublicInfo(storeId);
  return stores;
});

export interface ProductState {
  companyId: number | null | undefined;
  storeId: number | null;
  products: Product[];
  productsNext: string | null;
  productsPrevious: string | null;
  productsDictionary: ProductDictionary;
  cart: Product[]; // New cart array to store items added to the cart
  categories: Category[];
  loading: boolean;
  storeLoading: boolean;
  error?: string;
  priceMin: null | number;
  priceMax: null | number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  stores: any[] | null | undefined;
  storeInfo: StoreData | null | undefined;
  states: { id: string; name: string }[];
  variants: Product | null;
  banner: string[] | null;
  showDetails: boolean;
  selectedProduct: Product | null;
  isLightboxOpen: boolean;
}

const initialState: ProductState = {
  companyId: null,
  storeId: null,
  products: [],
  productsNext: null,
  productsPrevious: null,
  productsDictionary: {},
  categories: [],
  cart: [],
  loading: false,
  storeLoading: false,
  error: '',
  priceMin: null,
  priceMax: null,
  stores: undefined,
  storeInfo: undefined,
  states: [{ id: 'lagos', name: 'Lagos' }],
  variants: null,
  banner: null,
  showDetails: false,
  selectedProduct: null,
  isLightboxOpen: false,
};

const productSlice = createSlice({
  name: 'products',
  initialState,
  reducers: {
    setSelectedProduct: (state, action: PayloadAction<Product | null>) => {
      state.selectedProduct = action.payload;
      state.showDetails = action.payload ? true : false;
      state.isLightboxOpen = false;
    },
    openLightbox: (state) => {
      state.isLightboxOpen = true;
    },
    closeLightbox: (state) => {
      state.isLightboxOpen = false;
    },
    addToItem: (state, action: PayloadAction<number>) => {
      const itemIndex = state.cart.findIndex(
        (item) => item.id === action.payload
      );
      if (itemIndex !== -1) {
        if (state.cart[itemIndex].quantity) {
          state.cart[itemIndex].quantity += 1;
        }
      }
    },
    setPriceMax: (state, action: PayloadAction<number | null>) => {
      state.priceMax = action.payload;
    },
    setPriceMin: (state, action: PayloadAction<number | null>) => {
      state.priceMin = action.payload;
    },
    showVariants: (state, action: PayloadAction<Product>) => {
      state.variants = action.payload;
    },
    hideVariants: (state) => {
      state.variants = null;
    },
    addCompanyId: (state, action: PayloadAction<number>) => {
      state.companyId = action.payload;
    },
    addStoreId: (state, action: PayloadAction<number>) => {
      state.storeId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProducts.pending, (state) => {
        state.loading = true;
        state.error = '';
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        const products = action.payload.products || [];
        state.products = [...products];
        state.productsNext = action.payload.next || null;
        state.productsPrevious = action.payload.previous || null;
        const productDict: ProductDictionary = products.reduce(
          (acc: ProductDictionary, product: Product) => {
            acc[product.id] = product;
            return acc;
          },
          {}
        );
        state.productsDictionary = productDict;
        state.loading = false;
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to load products';
        toast.error(state.error);
      })
      .addCase(fetchCategories.pending, (state) => {
        state.loading = true;
        state.error = '';
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.categories = action.payload;
        state.loading = false;
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to load categories';
        toast.error(state.error);
      })
      .addCase(fetchStores.pending, (state) => {
        ((state.storeLoading = true), (state.loading = true));
        state.error = '';
      })
      .addCase(fetchStores.fulfilled, (state, action) => {
        state.stores = action.payload;
        state.storeLoading = false;
        state.loading = false;
      })
      .addCase(fetchStores.rejected, (state, action) => {
        state.loading = false;
        state.storeLoading = false;
        state.error = action.error.message || 'Failed to load stores';
        toast.error(state.error);
      })
      .addCase(fetchStoreInfo.pending, (state) => {
        state.loading = true;
        state.error = '';
      })
      .addCase(fetchStoreInfo.fulfilled, (state, action) => {
        state.storeInfo = action.payload;
        state.loading = false;
      })
      .addCase(fetchStoreInfo.rejected, (state, action) => {
        state.loading = false;
        state.storeInfo = null;
        state.error = action.error.message || 'Failed to load store info';
        toast.error(state.error);
      })
      .addCase(fetchStoreBanner.pending, (state) => {
        state.loading = true;
        state.error = '';
      })
      .addCase(fetchStoreBanner.fulfilled, (state, action) => {
        const banner =
          typeof action.payload === 'object' && action.payload.length !== 0
            ? action.payload
            : null;
        state.banner = banner;
        state.loading = false;
      })
      .addCase(fetchStoreBanner.rejected, (state, action) => {
        state.loading = false;
        state.banner = null;
        state.error = action.error.message || 'Failed to load store banner';
        toast.error(state.error);
      });
  },
});

export const {
  setPriceMin,
  setPriceMax,
  addToItem,
  addCompanyId,
  addStoreId,
  showVariants,
  hideVariants,
  setSelectedProduct,
  openLightbox,
  closeLightbox,
} = productSlice.actions;
export default productSlice.reducer;
